import React, { useEffect, useState } from "react";
import { useMediaQuery } from 'react-responsive';
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableCell,
  TableBody,
  TableCellLayout,
  tokens,
  makeStyles,
  useTableFeatures,
  useTableSort,
  TableColumnId,
  createTableColumn,
  Field,
  shorthands,
  Button,
} from "@fluentui/react-components";
import { SearchBox } from "@fluentui/react-search-preview";
import { ArrowSync24Filled, Open24Filled } from "@fluentui/react-icons";
import VisitDetails from "../forms/visitDetails";
import config from "../../app/services/config";
import t from "../services/i18n/i18n";
import * as microsoftTeams from "@microsoft/teams-js";
import '../style/list.css';

const useStyles = makeStyles({
  root: {
    backgroundColor: tokens.colorSubtleBackgroundHover,
  },
  cell: {
    display: "flex",
    justifyContent: "center",
  },
  fieldWrapper: {
    ...shorthands.padding(
        tokens.spacingVerticalMNudge,
        tokens.spacingHorizontalMNudge
    ),
  },
});

type TextCell = {
  label: string;
};

type QuestionCell = {
  label: string;
};

type Item = {
  key: string;
  RequestId: TextCell;
  OrganizationName: TextCell;
  OrganizationUnit: TextCell;
  VisitDate: TextCell;
  Status: TextCell;
  VisitPurpose: TextCell;
  VisitorName: TextCell;
  VisitorCompany: TextCell;
  Question1: QuestionCell;
  Question2: QuestionCell;
  Question3: QuestionCell;
  Question4: QuestionCell;
  Question5: QuestionCell;
  Question6: QuestionCell;
  Question7: QuestionCell;
  Question8: QuestionCell;
  Question9: QuestionCell;
  Question10: QuestionCell;
  Attendant: TextCell;
  WhoChanged: TextCell;
  ChangeDate: TextCell;
  ResidenceCountry: TextCell;
  VisitedCountries: TextCell;
  VisitedRegions: TextCell;
  Question8Visible: boolean;
  Question9Visible: boolean;
  VisitedRegionsVisible: boolean;
};

export function ListTab() {
  const styles = useStyles();
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<Item | null>(null);
  const [resultDialog, setResultDialog] = useState<string | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [userEmail, setUserEmail] = useState<string | null>(null); // Добавляем состояние для email

  const itsmUrl = config.ITSM_URL + '/pages/UI.php?operation=details&class=MhpVisitFoodProductionRequest&id=';

  useEffect(() => {
    microsoftTeams.app
        .initialize()
        .then(() => {
          microsoftTeams.app
              .getContext()
              .then((context) => {
                const email = context.user?.userPrincipalName;
                console.log("User Email:", email);
                setUserEmail(email || null); // Сохраняем email в состоянии
              })
              .catch((error) => {
                console.error("Ошибка при получении контекста:", error);
              });
        })
        .catch((error) => {
          console.error("Ошибка инициализации Teams SDK:", error);
        });
  }, []);

  useEffect(() => {
    if (userEmail) {
      fetchITSMData();
    } else {
      console.warn("Email пользователя не найден.");
    }
  }, [userEmail]);

  const fetchITSMData = async () => {
    // подстановка для локального тестирования
    //const email = userEmail == 'partenit@estcrm.onmicrosoft.com' ? 'mhp.food.attendant1@mailinator.com' : userEmail;
    const email = userEmail;
    try {
      const response = await fetch(config.BACKEND_ENDPOINT + "/list", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      console.log("Updated Items API Response:", result);

      if (!result.objects || Object.keys(result.objects).length === 0) {
        setItems([]); // Устанавливаем пустой массив
        return;
      }

      const transformedItems: Item[] = Object.entries(result.objects).map(
          ([_, entry]: [string, any]) => ({
            key: entry.key,
            RequestId: { label: entry.fields.friendlyname || "N/A" },
            OrganizationName: { label: entry.fields.title || "N/A" },
            OrganizationUnit: { label: entry.fields.organization_unit || "N/A" },
            Status: { label: entry.fields.status || "N/A" },
            VisitPurpose: { label: entry.fields.visit_purpose || "N/A" },
            VisitDate: { label: entry.fields.visit_date || "N/A" },
            VisitorName: { label: entry.fields.visitor_name || "N/A" },
            VisitorCompany: { label: entry.fields.visitor_company || "N/A" },
            Question1: { label: entry.fields.health_question_1 || "N/A" },
            Question2: { label: entry.fields.health_question_2 || "N/A" },
            Question3: { label: entry.fields.health_question_3 || "N/A" },
            Question4: { label: entry.fields.health_question_4 || "N/A" },
            Question5: { label: entry.fields.health_question_5 || "N/A" },
            Question6: { label: entry.fields.health_question_6 || "N/A" },
            Question7: { label: entry.fields.health_question_7 || "N/A" },
            Question8: { label: entry.fields.health_question_8 || "N/A" },
            Question9: { label: entry.fields.health_question_9 || "N/A" },
            Question10: { label: entry.fields.health_question_10 || "N/A" },
            Attendant: { label: entry.fields.attendant_id_friendlyname || "N/A" },
            WhoChanged: { label: entry.fields.latest_updater_id_friendlyname || "N/A" },
            ChangeDate: { label: entry.fields.last_update || "N/A" },
            ResidenceCountry: { label: entry.fields.residence_country_id_friendlyname || "N/A" },
            VisitedCountries: { label: entry.fields.last_visited_countries || "N/A" },
            VisitedRegions: { label: entry.fields.last_visited_regions || "N/A" },
            Question8Visible: entry.visible_health_question_8 ?? true,
            Question9Visible: entry.visible_health_question_9 ?? true,
            VisitedRegionsVisible: entry.visible_last_visited_regions ?? true,
          })
      );
      console.log('transformedItems', transformedItems);

      setItems(transformedItems); // Обновляем состояние items
    } catch (error) {
      // @ts-ignore
      console.error("Error fetching data:", error.message);
    }
  };

  const handleRefresh = () => {
    fetchITSMData(); // Повторный вызов для обновления данных
  };

  const handleAccept = async (key: string, reason: string) => {
    try {
      const response = await fetch(config.BACKEND_ENDPOINT + "/resolve", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, reason }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      console.log("Resolve API Response:", result);

      // Повторная загрузка данных
      await fetchITSMData();

      // Показываем успешное сообщение
      setResultDialog(t('form.result.resolve.success'));
    } catch (error) {
      // @ts-ignore
      console.error("Error handling accept:", error.message);
      setResultDialog(t('form.result.resolve.fail'));
    }
  };

  const viewDetails = (rowData: Item) => {
    setSelectedRowData(rowData);
    setIsDialogOpen(true);
  };

  const handleReject = async (key: string, reason: string) => {
    try {
      const response = await fetch(config.BACKEND_ENDPOINT + "/reject", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, reason }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      console.log("Reject API Response:", result);

      // Повторная загрузка данных
      await fetchITSMData();

      // Показываем успешное сообщение
      setResultDialog(t('form.result.reject.success'));
    } catch (error) {
      // @ts-ignore
      console.error("Error handling reject:", error.message);
      setResultDialog(t('form.result.reject.fail'));
    }
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setSelectedRowData(null);
  };

  const closeResultDialog = () => {
    setResultDialog(null);
  };

  const {
    getRows,
    sort: { getSortDirection, toggleColumnSort, sort },
  } = useTableFeatures(
      {
        columns: [
          createTableColumn<Item>({
            columnId: t('tabs.list.requestId'),
            compare: (a, b) => a.RequestId.label.localeCompare(b.RequestId.label),
          }),
          createTableColumn<Item>({
            columnId: t('tabs.list.orgName'),
            compare: (a, b) => a.OrganizationName.label.localeCompare(b.OrganizationName.label),
          }),
          createTableColumn<Item>({
            columnId: t('tabs.list.status'),
            compare: (a, b) => a.Status.label.localeCompare(b.Status.label),
          }),
          createTableColumn<Item>({
            columnId: t('tabs.list.visitPurpose'),
            compare: (a, b) => a.Status.label.localeCompare(b.VisitPurpose.label),
          }),
          createTableColumn<Item>({
            columnId: t('tabs.list.visitDate'),
            compare: (a, b) => a.Status.label.localeCompare(b.VisitDate.label),
          }),
          createTableColumn<Item>({
            columnId: "",
          }),
        ],
        items,
      },
      [
        useTableSort({
          defaultSortState: {
            sortColumn: "RequestId",
            sortDirection: "ascending",
          },
        }),
      ]
  );

  const headerSortProps = (columnId: TableColumnId) => ({
    onClick: (e: React.MouseEvent) => {
      toggleColumnSort(e, columnId);
    },
    sortDirection: getSortDirection(columnId),
  });

  const rows = sort(getRows());

  const filteredRows = rows.filter((row) =>
      Object.values(row.item).some((cell) => {
        // @ts-ignore
        if (cell && cell?.label) {
          // @ts-ignore
          return cell.label.toLowerCase().includes(searchQuery.toLowerCase());
        }
        return false;
      })
  );
  const isMobile = useMediaQuery({ maxWidth: 768 });

  return (
      <div>
        <div
            style={{
              backgroundColor: "#f4f4f4",
              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
              padding: "0px",
              marginBottom: "2px",
              zIndex: 20,
            }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <div>
              <Button icon={<ArrowSync24Filled />} onClick={handleRefresh}>
                {t('tabs.list.refresh')}
              </Button>
            </div>
            <div className={styles.fieldWrapper} style={{ marginLeft: "auto" }}>
              <Field>
                <SearchBox
                    size="medium"
                    placeholder="Пошук"
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
              </Field>
            </div>
          </div>
        </div>

        {items.length > 0 ? (
            <>
              {/* Проверяем ширину экрана */}
              {isMobile ? (
                  // Карточки для мобильных устройств
                  <div className="cardContainer">
                    {filteredRows.map((row, index) => (
                        <div key={index} className="card">
                          <div className="cardRow">
                            <span className="cardLabel">{t('tabs.list.requestId')}:</span>
                            <span className="cardValue">{row.item.RequestId.label}</span>
                          </div>
                          <div className="cardRow">
                            <span className="cardLabel">{t('tabs.list.orgName')}:</span>
                            <span className="cardValue">{row.item.OrganizationName.label}</span>
                          </div>
                          <div className="cardRow">
                            <span className="cardLabel">{t('tabs.list.visitPurpose')}:</span>
                            <span className="cardValue">{row.item.VisitPurpose.label}</span>
                          </div>
                          <div className="cardRow">
                            <span className="cardLabel">{t('tabs.list.visitDate')}:</span>
                            <span className="cardValue">{row.item.VisitDate.label}</span>
                          </div>
                          <div className="cardRow">
                            <span className="cardLabel">{t('tabs.list.visitorName')}:</span>
                            <span className="cardValue">{row.item.VisitorName.label}</span>
                          </div>
                          <div className="cardRow">
                            <span className="cardLabel">{t('tabs.list.status')}:</span>
                            <span className="cardValue">{t('tabs.list.status.' + row.item.Status.label)}</span>
                          </div>
                          <div className="cardActions">
                            <Button onClick={() => viewDetails(row.item)} className="table-button">
                              {t('tabs.list.details')}
                            </Button>
                            <a
                                href={itsmUrl + row.item.key}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                              <Button
                                  icon={<Open24Filled/>}
                                  title={t('tabs.list.goToItsm')}
                                  className="table-button"
                                  style={{marginLeft: '20px'}}
                              />
                            </a>
                          </div>
                        </div>
                    ))}
                  </div>

              ) : (
                  // Обычная таблица для десктопа
                  <Table aria-label="List table" className={styles.root}>
                    <TableHeader>
                      <TableRow>
                        {[t('tabs.list.requestId'), t('tabs.list.orgName'), t('tabs.list.visitPurpose'), t('tabs.list.visitDate'), t('tabs.list.visitorName'), t('tabs.list.status'), ""].map((columnId) => (
                            <TableHeaderCell key={columnId} {...headerSortProps(columnId)}>
                              <b>{columnId}</b>
                            </TableHeaderCell>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredRows.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <TableCellLayout>{row.item.RequestId.label}</TableCellLayout>
                            </TableCell>
                            <TableCell>
                              <TableCellLayout>{row.item.OrganizationName.label}</TableCellLayout>
                            </TableCell>
                            <TableCell>
                              <TableCellLayout>{row.item.VisitPurpose.label}</TableCellLayout>
                            </TableCell>
                            <TableCell>
                              <TableCellLayout>{row.item.VisitDate.label}</TableCellLayout>
                            </TableCell>
                            <TableCell>
                              <TableCellLayout>{row.item.VisitorName.label}</TableCellLayout>
                            </TableCell>
                            <TableCell>
                              <TableCellLayout>{t('tabs.list.status.' + row.item.Status.label)}</TableCellLayout>
                            </TableCell>
                            <TableCell>
                              <Button onClick={() => viewDetails(row.item)} className="table-button">
                                {t('tabs.list.details')}
                              </Button>
                              <a href={itsmUrl + row.item.key} target="_blank" rel="noopener noreferrer">
                                <Button icon={<Open24Filled />} title={t('tabs.list.goToItsm')} className="table-button"
                                        style={{ marginLeft: '20px' }} />
                              </a>
                            </TableCell>
                          </TableRow>
                      ))}
                    </TableBody>
                  </Table>
              )}
            </>
        ) : (
            <div style={{ textAlign: "center", marginTop: "20px" }}>
              <h3>{t('tabs.list.noRecords')}</h3>
            </div>
        )}

        {/* Диалоги */}
        {isDialogOpen && selectedRowData && (
            <div className="dialog-overlay">
              <div className="dialog-container">
                <VisitDetails
                    data={selectedRowData}
                    onClose={closeDialog}
                    onAccept={(reason) => handleAccept(selectedRowData.key, reason)}
                    onReject={(reason) => handleReject(selectedRowData.key, reason)} // Передаём причину
                />
              </div>
            </div>
        )}

        {resultDialog && (
            <div className="dialog-overlay">
              <div className="dialog-container">
                <div className="result-dialog-body">
                  <h2>{t('form.result.header')}</h2>
                  <button className="close-button" onClick={closeResultDialog}>
                    &#9587;
                  </button>
                  <p>{resultDialog}</p>
                </div>
              </div>
            </div>
        )}
      </div>
  );

}
